// @ts-nocheck

import moment from 'moment-timezone';
import * as R from 'ramda';
import {
  DEFAULT_DATE_FORMAT,
  FIRST_DRUID_DATE,
  TIMEZONE_FORMAT,
  UNIX_SECONDS_FORMAT,
  WITH_OFFSET_TIMESTAMP_FORMAT,
} from './dateUtils';
const { TIMEZONES } = window;
const millisecondsInAMinute = 60 * 1000;
const minutesInAnHour = 60;
const hoursInADay = 24;
const millisecondsInADay = hoursInADay * minutesInAnHour * millisecondsInAMinute;
const warehouseSLA = 5;
const timeToIndexInDruid = 1;

/* -------------------------------------------------------------------------
To use a date utility METHOD or CONSTANT in a component, see dateUtils.js
------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------
PRIVATE method definitions and documentation:

These are all the private function definitions used to create the public
functions which are exported in dateUtils.js

They are exported in order to use in unit tests and by dateUtils,
not for use in components.

Double underscore is to signify these methods aren't used anywhere
but within this file (and used for unit tests).
------------------------------------------------------------------------- */

/** Returns the timestamp provided in inputFormat in the outputFormat
 * without any timezone conversion
 *
 * @param {string} timestamp in inputFormat- example: '1:25:11 PM'
 * @param {string} inputFormat - example: 'hh:mm:ss A'
 * @param {string} outputFormat - example: 'HH:mm:ss'
 * @return {string} timestamp in outputFormat - example: '13:25:11'
 */
export const _convertFormat = (timestamp: string, inputFormat: string, outputFormat: string) => {
  return moment(timestamp, [inputFormat]).format(outputFormat);
};

/** Returns the truncated date of the timestamp entered,
 * without any timezone information
 *
 * @param {string} timestamp string - example: 2017-10-28T22:00:00-07:00
 * @return {string} timestamp string - example: 2017-10-28
 */
export const __getJustDate = (timestamp: string): string | null | undefined => {
  if (typeof timestamp === 'string' && timestamp.indexOf('T') !== -1) {
    return timestamp.split('T')[0];
  }

  return timestamp;
};

/** Returns the date entered, prefixed with today's year in the timezone entered,
 * if it didn't have one already. Otherwise returns what was entered.
 * NOTE: IMPURE FUNCTION - Relies on today's date
 *
 * @param {string} user-entered timestamp string - example: '10/22'
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @return {string} timestamp string - example:
 * if today's date is after May in 2017 OR before May in 2018: '2017/10/22'.
 */
export const _prefixWithYear = (timestamp: string, timezone: string): string => {
  const splitDate = timestamp.split('/');

  if (typeof timestamp === 'string' && splitDate && splitDate.length === 2) {
    const today = moment();
    // This will get the current year in the timezone passed in
    let year = moment.tz(today, timezone).format('YYYY');
    // We support fetching 6 months of data behind today. If today's date is
    // earlier than June AND the user types a date anytime July -> Dec,
    // they must mean last year, not the current year, so subtract one.
    // Ex: if today's date is Jan 15 2017 and the user types '12/19',
    // we need to return '2017/12/19' not '2018/12/19'.
    const todaysMonth = Number(today.format('M'));
    const enteredDatesMonth = Number(splitDate[0]);

    if (todaysMonth <= 6 && enteredDatesMonth > 6) {
      year--;
    }

    // Add missing year before rest of timestamp
    return `${year}/${timestamp}`;
  }

  return timestamp;
};

/** Converts the time entered into the timezone specified
 * NOTE: The timestamp in and timestamp out ARE equivalent times
 *
 * @param {string} timestamp string - example: 2017-10-28T22:00:00-07:00
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @return {string} timestamp string - example: 2017-10-29T12:45:00+05:45
 */
export const _convertToTimezone = (
  timestamp: string | number,
  timezone: string,
  format: string = WITH_OFFSET_TIMESTAMP_FORMAT
): string => moment.tz(timestamp, timezone).format(format);
export type CalendarFormat = {
  sameDay?: string;
  nextDay?: string;
  nextWeek?: string;
  lastDay?: string;
  lastWeek?: string;
  sameElse?: string;
};

/** Converts the time entered into the timezone specified
 * NOTE: The timestamp in and timestamp out ARE equivalent times
 *
 * @param {string} timestamp string - example: 2017-10-28T22:00:00-07:00
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @return {string} timestamp string - example: Yesterday at 4:12 PM
 * @param {string} refTimestamp string - example: 2017-10-28T22:00:00-07:00
 */
export const _convertToCalendarTimezone = (
  timestamp: string,
  timezone: string,
  format: CalendarFormat = {},
  refTimestamp: string = new Date().toISOString()
): string => moment.tz(timestamp, timezone).calendar(refTimestamp, format);

/** Returns the DATE entered, but at midnight in the timezone specified.
 * NOTE: The timestamp in and timestamp out are NOT equivalent times
 * but will always be the same date.
 *
 * @param {string} timestamp string - example: 2017-10-28T22:00:00-07:00
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @param {string} optional moment format - example: 'MMM Do YYYY hA'
 * @return {string} timestamp string - example: 2017-10-28T00:00:00+05:45
 */
export const _getFirstMoment = (
  timestamp: string,
  timezone: string,
  format: string = WITH_OFFSET_TIMESTAMP_FORMAT
): string => {
  return moment.tz(__getJustDate(timestamp), timezone).format(format);
};

/** Returns the DATE entered, but at 23:59 in the timezone specified.
 * NOTE: The timestamp in and timestamp out are NOT equivalent times
 * but will always be the same date.
 *
 * @param {string} timestamp string - example: 2017-10-28T22:00:00-07:00
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @param {string} optional moment format - example: 'MMM Do YYYY hA'
 * @return {string} timestamp string - example for Asia/Kathmandu timezone:
 * 2017-10-28T23:59:59+05:45
 */
export const _getLastMoment = (timestamp: string, timezone: string, format: string = WITH_OFFSET_TIMESTAMP_FORMAT) => {
  return moment.tz(__getJustDate(timestamp), timezone).hour(23).minute(59).second(59).millisecond(999).format(format);
};

/** Returns an object with start_date (from earliest moment in the start day)
 * and end_date (to last moment in the end day) for the given timezone
 *
 * @param {string} Any timezone - example: 'Asia/Kathmandu'
 * @param {string} The date the range should end with (often will pass in
 * current date (`moment()`) to get 'Last _ Days' range)
 * @param {number} number of granularity units the range should be for - example: 7
 * @param {string} what period each unit in the duration ^ should be, can be
 * any unit moment library can accept - examples: 'year', 'days' (default)
 * @return {Range} object with start_date and end_date
 */
export type Range = {
  start_date: string;
  end_date: string;
};
export const _getRangeInTimezone = (
  timezone: string,
  endDate: string,
  duration: number,
  granularity: string = 'days',
  format: string = WITH_OFFSET_TIMESTAMP_FORMAT
): Range => {
  const start = moment.tz(endDate, timezone).subtract(duration, granularity).format();
  const end = moment.tz(endDate, timezone).format();
  return {
    start_date: _getFirstMoment(start, timezone, format),
    end_date: _getLastMoment(end, timezone, format),
  };
};

/**
 * Get the time difference between the browserTimezone
 * and appTimezone in milliseconds
 * @param {Date} tick moment object
 * @param {string} appTimezone timezone set by the app
 * @param {string} browserTimezone timezone detected by moment.tz.guess()
 * @return {number} number of milliseconds of difference between two timezones
 */
export const __getBrowserAppTimezoneOffset = (
  tick: Record<string, any>,
  appTimezone: string,
  browserTimezone: string
): number => {
  const appTimezoneOffset = moment.tz(tick, appTimezone).utcOffset();
  const browserTimezoneOffset = moment.tz(tick, browserTimezone).utcOffset();
  return (browserTimezoneOffset - appTimezoneOffset) * millisecondsInAMinute;
};

/**
 * Apply an offset to adjust time between browserTimezone and appTimezone
 * for every tick
 * @param {Array<Date>} tickValues Date objects
 * @param {string} appTimezone timezone set by the app
 * @param {string} browserTimezone timezone detected by moment.tz.guess()
 * @return {Array<Date>} list of times after applying the offset
 */
export const _applyTimezoneOffsetToTickValues = (
  tickValues: Array<Date>,
  appTimezone: string,
  browserTimezone: string
): Array<Date> => {
  return tickValues.map((tick) => {
    const offset = __getBrowserAppTimezoneOffset(tick, appTimezone, browserTimezone);

    return new Date(tick.getTime() + offset);
  });
};

/**
 * Add an extra tick at the beginning if appTimezone is behind (negative)
 * browserTimezone
 * Add an extra tick at the end if appTimezone is ahead (positive) of
 * browserTimezone
 * @param {Array<Date>} tickValues Date objects
 * @param {string} appTimezone timezone set by the app
 * @param {string} browserTimezone timezone detected by moment.tz.guess()
 * @return {Array<Date>} list of times with an addition at beginning and/or end
 */
export const _addFirstOrLastTick = (
  tickValues: Array<Record<string, any>>,
  appTimezone: string,
  browserTimezone: string
): Array<Record<string, any>> => {
  const offsetFirst = __getBrowserAppTimezoneOffset(tickValues[0], appTimezone, browserTimezone);

  const offsetLast = __getBrowserAppTimezoneOffset(tickValues[tickValues.length - 1], appTimezone, browserTimezone);

  if (offsetLast < 0) {
    return tickValues.concat(new Date(tickValues[tickValues.length - 1].getTime() + millisecondsInADay));
  }

  if (offsetFirst > 0) {
    return [new Date(tickValues[0].getTime() - millisecondsInADay)].concat(tickValues);
  }

  return tickValues;
};
type Timezones = Array<{
  name: string;
  label: string;
  facebookId?: number;
}>;

/**
 * Generates an array of timezones that matches the saved timezone list
 * but adds the current offset (as of today) for each timezone to the label.
 * NOTE: IMPURE FUNCTION - Relies on today's date
 *
 * @param {Array<Timezones>} Original timezones list we have saved hard-coded
 * @return {Array<Timezones>} List of timezones with offset included in label
 */
export const _getTimezoneListWithOffsets = (): Timezones => {
  return TIMEZONES.map((timezone) => {
    const { name } = timezone;
    const timezoneOffset = moment.tz(new Date(), name).format('Z');
    const label = `(GMT${timezoneOffset}) ${timezone.label}`;
    return {
      name,
      label,
    };
  });
};

/**
 * Sorts an array of timezones in offset order, from most negative (GMT-10:00)
 * to most positive (GMT+13:00) (therefore GMT itself is in the middle).
 *
 * @param {Array<Timezones>} List of timezones with offset included in label
 * @return {Array<Timezones>} List of timezones in offset order
 */
export const _sortTimezonesByOffset = (timezonesWithOffset: Timezones): Timezones => {
  const descending = R.sort(R.descend(R.prop('label')))(timezonesWithOffset);
  const crossoverPoint = R.findIndex((timezone) => {
    return timezone.label.indexOf('+') > 0;
  })(descending);
  const negativeOffsetsSorted = descending.slice(0, crossoverPoint - 1);
  const positiveOffsets = descending.slice(crossoverPoint);
  const positiveOffsetsSorted = R.reverse(positiveOffsets);
  return [...negativeOffsetsSorted, ...positiveOffsetsSorted];
};

/**
 * Given the offset of guess for the browser's timezone, returns an object
 * representing the timezone in our list closest to that browser's timezone
 * NOTE: IMPURE FUNCTION! Relies on today's date
 *
 * @param {number} the offset of the best guess for the browser's timezone
 * @return {Object} object with offsetDiff and name properties
 */
type Closest = {
  offsetDiff: number;
  name: string | null | undefined;
};

const __getClosest = (guessOffset: number): Closest => {
  return TIMEZONES.reduce(
    (
      closest: Closest,
      timezone: {
        name: string;
      }
    ): Closest => {
      // if we've already found a timezone with no difference from our guess,
      // that will be our answer no matter what.
      if (closest.offsetDiff === 0) {
        return closest;
      }

      const { name } = timezone;
      const timezoneOffset = moment.tz.zone(name).offset(new Date());
      const offsetDiff = Math.abs(guessOffset - timezoneOffset);

      // if no timezone has been chosen yet, set it from first timezone
      // or if we've found a new closest timezone, set it as closest
      if (!closest.name || offsetDiff < closest.offsetDiff) {
        return {
          offsetDiff,
          name,
        };
      }

      return closest;
    },
    {
      offsetDiff: Infinity,
      name: null,
    }
  );
};

/**
 * Fetches the first day of the week. We use Sunday as the first day of the week.
 */
export const __getFirstDayOfWeek = (timestamp: string, timezone: string, format: string = DEFAULT_DATE_FORMAT): string => {
  const momentObj = moment.tz(timestamp, timezone);
  const daysFromSunday = momentObj.day();
  return momentObj.clone().subtract(daysFromSunday, 'days').format(format);
};

/**
 * Fetches the last day of the week. We use Saturday as the last day of the week.
 */
export const __getLastDayOfWeek = (timestamp: string, timezone: string, format: string = DEFAULT_DATE_FORMAT): string => {
  const momentObj = moment.tz(timestamp, timezone);
  // moment represents saturday as 6
  const daysFromSaturday = 6 - momentObj.day();
  return momentObj.clone().add(daysFromSaturday, 'days').format(format);
};

/**
 * Returns the closest timezone in our list to the best guess of the
 * browser's timezone, based on the current offset of each timezone
 * NOTE: IMPURE FUNCTION! Relies on today's date
 *
 * @return {string} Always will be one of the timezones in our saved list
 */
export const _getBestGuessInList = (): string | null | undefined => {
  const guess = moment.tz.guess();

  if (!guess) {
    return null;
  }

  const guessIsInList = !!R.find(R.propEq('name', guess))(TIMEZONES);

  // if the guess from moment is already in our timezone list, return that
  if (guessIsInList) {
    return guess;
  }

  const guessOffset = moment.tz.zone(guess).offset(new Date());

  const closest = __getClosest(guessOffset);

  return closest.name || guess;
};
export const _getMomentFromOffset = (
  offset: number,
  startDate: string,
  granularity: string,
  appTimezone: string
): Record<string, any> => {
  const startDateMoment = moment.tz(startDate, appTimezone);
  return startDateMoment.clone().add(offset, granularity);
};

/**
 * method that return the amount of time in hours it takes for cohort data to successfully appear on the dashboard from the end of the day on which the event occurred.
 * The calculation is as follows: 24 hrs + timezone offset of current app + 12 hrs + hours needed for data to appear in the data warehosue + hours needed to index data in Druid
 */
export const _getCollectionWindow = (timestamp: string, appTimezone: string) => {
  const momentObj = moment.tz(timestamp, appTimezone);
  return (
    hoursInADay +
    moment.tz(momentObj, appTimezone).utcOffset() / minutesInAnHour +
    hoursInADay / 2 +
    warehouseSLA +
    timeToIndexInDruid
  );
};

/**
 * Method that gets the label for a given timezone name
 * i.e. _getLabelForTimezoneName('America/Los_Angeles') => '(GMT-07:00) America/Los Angeles'
 */
export const _getLabelForTimezoneName = (timezoneList: Timezones, timezone: string) => {
  const timezoneObject = timezoneList.find(R.propEq('name', timezone));
  return timezoneObject ? timezoneObject.label : null;
};
// [EG-1165] minimum date determined by two_year_data_retention flipper value
// true => 24 months before now, false => 6 months before now
// returns FIRST_DRUID_DATE if the calculated minimum date is before then
export const _determineDefaultMinimumDate = (currentDate: Record<string, any>, two_year_data_retention: boolean) => {
  const minDate = two_year_data_retention
    ? moment(currentDate).subtract(24, 'months')
    : moment(currentDate).subtract(6, 'months');
  return minDate.isBefore(FIRST_DRUID_DATE) ? moment(FIRST_DRUID_DATE).toDate() : minDate.toDate();
};
export const _isMomentBridged = (date: string, startDate: string, endDate: string, appTimezone: string) => {
  const dateSeconds = _convertToTimezone(date, appTimezone, UNIX_SECONDS_FORMAT);

  const startDateSeconds = _convertToTimezone(startDate, appTimezone, UNIX_SECONDS_FORMAT);

  const lastMoment = _getLastMoment(endDate, appTimezone);

  const endDateSeconds = _convertToTimezone(lastMoment, appTimezone, UNIX_SECONDS_FORMAT);

  return dateSeconds > startDateSeconds && dateSeconds < endDateSeconds;
};
export function _getTimeZoneAbbr(timezone: string) {
  return moment.tz(timezone).format(TIMEZONE_FORMAT);
}
